import { Dialog } from "components/Dialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import { FacilityChemicalStorageLocationInputWithIssues } from "components/StorageLocationsDataGrid";
import { storageUnitMustMatchChemicalUnit } from "encamp-shared/src/constants/tierii";
import { parseFlexibleFloat } from "encamp-shared/src/utils/flexibleFloat";
import { storageTypeFields } from "encamp-shared/src/utils/storageTypeFields";
import {
  FacilityChemicalStorageLocationInput,
  Issue,
  Pressure,
  Temperature,
  UnitType,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { FacilityChemicalStorageLocationForm } from "routes/Customer/Chemicals/Inventory/Facility/FacilityChemicalStorageLocationForm";
import { useFacility } from "routes/Customer/Facility/useFacility";
import invariant from "tiny-invariant";
import { hasCriticalIssues } from "util/forms";
import { v4 as uuidv4 } from "uuid";

import { useFacilityChemicalStorageLocationInputValidation } from "../../Report/validationHooks/useFacilityChemicalStorageLocationValidation";
import { FacilityChemicalDetailFormType } from "./FacilityChemicalAddEditDialog";
import { StorageLocationForm } from "./StorageLocationForm";
import { type ChemicalDensity } from "./storageLocationUnitSync";

export type FacilityChemicalStorageLocationDialogProps = {
  facilityId: string;
  facilityChemicalId: string;
  facilityChemical?: FacilityChemicalDetailFormType;
  chemicalDensity?: ChemicalDensity;
  initialValues: FacilityChemicalStorageLocationInputWithIssues | null;
  onSubmit: (newValue: FacilityChemicalStorageLocationInput) => void;
  open: boolean;
  loading: boolean;
  onClose: () => void;
  disabled?: boolean;
};

/**
 * The amount inputs hold typed text; the parent grid and its formatters expect
 * numbers. Parse as the validator does, so any amount it accepted is a number.
 */
function withParsedAmounts(
  data: FacilityChemicalStorageLocationInput
): FacilityChemicalStorageLocationInput {
  return {
    ...data,
    maxAmount: parseFlexibleFloat(data.maxAmount),
    averageAmount: parseFlexibleFloat(data.averageAmount),
    maxAmountLargestContainer: parseFlexibleFloat(data.maxAmountLargestContainer),
  };
}

export function FacilityChemicalStorageLocationDialog(
  props: FacilityChemicalStorageLocationDialogProps
) {
  const { data: facilityData } = useFacility(props.facilityId);
  const { facilityId, disabled, initialValues, facilityChemicalId, facilityChemical } = props;
  invariant(facilityId);
  const isEditMode = initialValues !== null;

  const facilityState = facilityData?.facility?.state;
  const parentChemicalUnit = facilityChemical?.unit ?? null;
  const unitDerivedFromChemical =
    storageUnitMustMatchChemicalUnit(facilityState) && parentChemicalUnit != null;

  const defaultValues: FacilityChemicalStorageLocationInput = useMemo(
    () => ({
      id: props.initialValues?.id ?? uuidv4(),
      maxAmount: props.initialValues?.maxAmount,
      pressure: props.initialValues?.pressure ?? (isEditMode ? null : Pressure.AmbientPressure),
      otherPressureValue: props.initialValues?.otherPressureValue ?? null,
      temperature:
        props.initialValues?.temperature ?? (isEditMode ? null : Temperature.AmbientTemperature),
      storageType: props.initialValues?.storageType ?? "",
      // Derive via the helper so an existing row with storageTypes=[] but a
      // legacy scalar storageType still loads the multi-select (empty array
      // must fall back to [storageType], which `??` would not do).
      storageTypes: storageTypeFields({
        storageType: props.initialValues?.storageType,
        storageTypes: props.initialValues?.storageTypes,
      }).storageTypes,
      storageTypeDescription: props.initialValues?.storageTypeDescription ?? null,
      storageLocation: props.initialValues?.storageLocation
        ? {
            ...props.initialValues?.storageLocation,
            latitude:
              props.initialValues.storageLocation.latitude ?? facilityData?.facility?.latitude,
            longitude:
              props.initialValues.storageLocation.longitude ?? facilityData?.facility?.longitude,
          }
        : {
            description: "",
            facilityId,
            id: uuidv4(),
            OR_insideOutside: null,
            OR_storageArea: "",
            OR_storageBuilding: "",
            OR_storageQuadrant: null,
            OR_storageFloor: "",
            OR_storageRoom: "",
            NV_insideOutside: null,
            latitude: facilityData?.facility?.latitude,
            longitude: facilityData?.facility?.longitude,
          },
      unit: unitDerivedFromChemical
        ? parentChemicalUnit
        : (props.initialValues?.unit ?? (isEditMode ? null : UnitType.Pounds)),
      averageAmount: props.initialValues?.averageAmount,
      maxAmountLargestContainer: props.initialValues?.maxAmountLargestContainer,
    }),
    [
      facilityData?.facility?.latitude,
      facilityData?.facility?.longitude,
      facilityId,
      props.initialValues,
      isEditMode,
      unitDerivedFromChemical,
      parentChemicalUnit,
    ]
  );

  /**
   * This component depends on issues for the storage location to be included
   * directly on the object itself. Sometimes those issues come from other
   * schemas and have a key that looks like: key 'storageLocations.0.unit`. This
   * transform will ensure we convert the issues to look like 'unit' which
   * allows react-hook-form to properly map the issues to the inputs in this
   * dialog
   */
  const transformIssueKeys = (issues: Issue[]): Issue[] => {
    return issues.map((issue) => {
      if (issue.modelName === "FacilityChemicalStorageLocation") {
        return {
          ...issue,
          key: issue.key ? issue.key.split(".").pop() || issue.key : issue.key,
        };
      }
      return issue;
    });
  };

  const form = useValidatingForm<FacilityChemicalStorageLocationInput>(
    defaultValues,
    transformIssueKeys(initialValues?.issues ?? []),
    useFacilityChemicalStorageLocationInputValidation(
      facilityId,
      facilityChemicalId,
      facilityChemical
    )
  );

  const {
    handleSubmit,
    issues,
    formState: { isDirty },
  } = form;

  const context = isEditMode ? "Save" : "Add";

  return (
    <Dialog
      open={props.open}
      title={`${context} Storage Location`}
      onClose={props.onClose}
      additionalPrimaryActionElements={[
        <IssueListButton issues={issues} key="issue-list-button" />,
      ]}
      primaryActionButtonProps={{
        loading: props.loading,
        saveText: context,
        disabled: hasCriticalIssues(issues) || (!isDirty && !initialValues?.id),
        onClick: handleSubmit((data) => props.onSubmit(withParsedAmounts(data))),
      }}
    >
      <FormProvider {...form}>
        <StorageLocationForm facilityId={facilityId} disabled={disabled} />
        <FacilityChemicalStorageLocationForm
          disabled={disabled}
          parentChemicalUnit={facilityChemical?.unit}
          chemicalDensity={props.chemicalDensity}
        />
      </FormProvider>
    </Dialog>
  );
}
